/**
 * A page's permissions by level (owner's permissions plan, 3 Oct 2026): the key that opens each page, the kind of every
 * permission (new ones from the catalogue, the older ones by their name), the page's shortcuts (View only / Staff /
 * Full) and the level a role holds on a page - the "who can do what" table's words.
 */
import { PERMISSION_GROUPS } from './permissions';
import { ADMIN_LOCKED, NEW_KEYS, PRESETS } from './permissionCatalog';

// the permission that opens each page (the first level)
export const PAGE_OPEN = {
    dashboard: 'view_dashboard', kpi: 'view_kpi_dashboard', fleet: 'view_fleet_list', contracts: 'view_contracts',
    monthly_calculator: 'open_monthly_calculator', booking_management: 'view_booking_management',
    daily_booking: 'view_daily_booking', airport_dashboard: 'view_airport_dashboard', staff: 'view_staff',
    maintenance: 'view_maintenance', fleet_service_history: 'view_service_history', invoices: 'view_invoices',
    operations: 'view_operations', custodies: 'view_custodies', government_transactions: 'view_government_transactions',
    branch_charges: 'view_branch_charges', matcher: 'view_matcher', history: 'view_history',
    traffic_fines: 'view_traffic_fines', engine: 'view_engine', automation: 'use_automation', database: 'view_database',
    admin: 'manage_users',
};
const OPENERS = new Set(Object.values(PAGE_OPEN));

// the older permissions whose name does not tell their kind
const KIND_OF = {
    // manage_engine_schedule opens Admin > Schedules (UserManagement): an admin power, never in View only / Staff
    control_engine: 'run', manage_darb_session: 'edit', manage_engine_schedule: 'admin', ask_assistant: 'see',
    import_staff: 'edit', manage_roles: 'admin', manage_kpi_settings: 'edit', sync_kpi_data: 'run',
    view_kpi_ops: 'tab', view_kpi_employees: 'tab', view_kpi_fleet: 'tab', view_kpi_contracts: 'tab', view_kpi_financial: 'tab',
    view_invoice_salik: 'tab', view_invoice_parking: 'tab', view_invoice_traffic: 'tab',
    view_service_tab: 'tab', view_inventory_tab: 'tab', view_upcoming_tab: 'tab', view_inventory_purchase_history: 'tab',
    view_incomplete_maintenance: 'tab', view_completed_maintenance: 'tab', manage_automation_settings: 'admin',
    regenerate_fine_images: 'run',
    // the whole database as a zip (fleet, staff, contracts, archive ... names and phones): never in View only / Staff
    view_ops_backup: 'admin',
};

/** The kind of a permission: open / tab / see / edit / delete / export / ejar / run / send / admin. */
export const kindOf = (key) => {
    if (OPENERS.has(key)) return 'open';
    if (NEW_KEYS[key]) return NEW_KEYS[key].kind;
    if (KIND_OF[key]) return KIND_OF[key];
    if (/_tab_|^history_tab_|^view_ops_/.test(key)) return 'tab';
    if (/^(export_|share_)|_export_|generate_/.test(key)) return 'export';
    if (/^(delete_|reset_)|_delete_/.test(key)) return 'delete';
    if (/^(run_|sync_|pull_|regenerate_)/.test(key)) return 'run';
    if (/^register_|on_ejar$/.test(key)) return 'ejar';
    if (/^write_|^manage_(users|roles)$/.test(key)) return 'admin';
    if (/^(view_|search_|analyze_|preview_)/.test(key)) return 'see';
    return 'edit';
};

// a page's grantable permissions (the Admin role's own powers are no level of anyone else's)
export const pageKeys = (pageId) => (PERMISSION_GROUPS[pageId] || []).filter((k) => !ADMIN_LOCKED.has(k));

// a permission only the Admin role holds today (catalogue `today: 'admin'`) is given by "Full" only
const adminOnly = (key) => NEW_KEYS[key]?.today === 'admin';

/** The permissions a shortcut gives on a page ('none' = none of them). */
export const presetKeys = (pageId, preset) => (preset === 'none' || !PRESETS[preset]
    ? [] : pageKeys(pageId).filter((k) => PRESETS[preset].kinds.includes(kindOf(k)) && (preset === 'full' || !adminOnly(k))));

/**
 * The shortcuts worth showing on a page: [preset, ...] from none to full. "None" always; a shortcut that gives nothing,
 * or exactly what the one above it gives, is left out (the higher name stays) - a page of admin powers only has
 * "None" and "Full", never "View only" / "Staff" buttons that clear everything (review 3 Oct 2026).
 */
export const presetChoices = (pageId) => {
    const order = ['none', 'view', 'staff', 'full'];
    const sig = (p) => presetKeys(pageId, p).slice().sort().join(',');
    return order.filter((p, i) => p === 'none'
        || (sig(p) !== '' && (i === order.length - 1 || sig(p) !== sig(order[i + 1]))));
};

export const LEVEL_WORDS = { none: 'No access', view: 'View only', staff: 'Staff', full: 'Full', custom: 'Custom' };
export const LEVEL_TONE = { none: 'neutral', view: 'info', staff: 'accent', full: 'success', custom: 'warning' };

/** The level a permission list holds on a page: none / view / staff / full, or custom when it is no shortcut. */
export const levelOf = (permissions, pageId) => {
    const held = new Set(permissions || []);
    const keys = pageKeys(pageId);
    const mine = keys.filter((k) => held.has(k));
    if (!mine.length) return 'none';
    const same = (list) => list.length === mine.length && list.every((k) => held.has(k));
    for (const preset of ['full', 'staff', 'view']) {
        if (same(presetKeys(pageId, preset))) return preset;
    }
    return 'custom';
};
